import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";

import { md3Expressive } from "../../theme/md3Expressive";
import {
  getAccentThemeOverrides,
  type ThemeAccentId,
} from "../../theme/themeAccentPalettes";
import { tokens, type AppTokens, type ThemeColors } from "../../theme/tokens";

const STORAGE_KEYS = {
  darkMode: "bilingua_dark_mode",
  accent: "bilingua_theme_accent",
  highContrast: "bilingua_high_contrast",
} as const;

export type AppTheme = Omit<AppTokens, "colors"> & {
  colors: ThemeColors;
  isDarkMode: boolean;
  isHighContrast: boolean;
  accentId: ThemeAccentId;
};

type ThemeContextValue = {
  theme: AppTheme;
  isDarkMode: boolean;
  isHighContrast: boolean;
  accentId: ThemeAccentId;
  toggleDarkMode: () => void;
  setAccentId: (id: ThemeAccentId) => void;
  toggleHighContrast: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function buildTheme(
  isDarkMode: boolean,
  accentId: ThemeAccentId,
  isHighContrast: boolean
): AppTheme {
  const baseColors = isDarkMode ? tokens.colorsDark : tokens.colors;
  const accentOverrides = getAccentThemeOverrides(accentId, isDarkMode);

  let highContrastOverrides: Partial<ThemeColors> = {};
  if (isHighContrast && !isDarkMode) {
    highContrastOverrides = {
      border: "#000000",
      text: "#000000",
      textSecondary: "#1e293b",
      textTitle: "#000000",
      textBase: "#000000",
      textComplement: "#1e293b",
    };
  } else if (isHighContrast && isDarkMode) {
    highContrastOverrides = {
      border: "#ffffff",
      text: "#ffffff",
      textSecondary: "#e2e8f0",
      textTitle: "#ffffff",
      textBase: "#ffffff",
      textComplement: "#e2e8f0",
    };
  }

  const colors: ThemeColors = {
    ...baseColors,
    primary: accentOverrides.primary ?? baseColors.primary,
    primaryLight: accentOverrides.primaryLight ?? baseColors.primaryLight,
    primaryDark: accentOverrides.primaryDark ?? baseColors.primaryDark,
    info: accentOverrides.info ?? baseColors.info,
    accent: accentOverrides.accent ?? baseColors.accent,
    textTitle: baseColors.text,
    textComplement: baseColors.textSecondary,
    textBase: baseColors.text,
    card: baseColors.surface,
    lineInWhite: baseColors.border,
    inputBackground: baseColors.surfaceSecondary,
    boxBase: baseColors.surface,
    boxFooter: baseColors.surfaceSecondary,
    layoutBg: baseColors.backgroundSecondary,
    ...highContrastOverrides,
  };

  return {
    ...tokens,
    md3: md3Expressive,
    colors,
    isDarkMode,
    isHighContrast,
    accentId,
  };
}

function readStored<T extends string>(
  key: string,
  fallback: T,
  valid: readonly T[]
): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw && valid.includes(raw as T)) return raw as T;
  } catch {
    /* ignore */
  }
  return fallback;
}

function readBool(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch {
    /* ignore */
  }
  return fallback;
}

const ACCENT_IDS: ThemeAccentId[] = [
  "purple",
  "pink",
  "blue",
  "green",
  "orange",
];

function resolveSystemDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDarkMode, setIsDarkMode] = useState(() =>
    readBool(STORAGE_KEYS.darkMode, resolveSystemDark())
  );
  const [accentId, setAccentIdState] = useState<ThemeAccentId>(() =>
    readStored(STORAGE_KEYS.accent, "purple", ACCENT_IDS)
  );
  const [isHighContrast, setIsHighContrast] = useState(() =>
    readBool(STORAGE_KEYS.highContrast, false)
  );

  const theme = useMemo(
    () => buildTheme(isDarkMode, accentId, isHighContrast),
    [isDarkMode, accentId, isHighContrast]
  );

  useEffect(() => {
    document.documentElement.dataset.theme = isDarkMode ? "dark" : "light";
    document.documentElement.dataset.accent = accentId;
    if (isHighContrast) {
      document.documentElement.dataset.contrast = "high";
    } else {
      delete document.documentElement.dataset.contrast;
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute("content", theme.colors.primary);
    }
  }, [isDarkMode, accentId, isHighContrast, theme.colors.primary]);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEYS.darkMode, String(next));
      return next;
    });
  }, []);

  const setAccentId = useCallback((id: ThemeAccentId) => {
    setAccentIdState(id);
    localStorage.setItem(STORAGE_KEYS.accent, id);
  }, []);

  const toggleHighContrast = useCallback(() => {
    setIsHighContrast((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEYS.highContrast, String(next));
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      isDarkMode,
      isHighContrast,
      accentId,
      toggleDarkMode,
      setAccentId,
      toggleHighContrast,
    }),
    [
      theme,
      isDarkMode,
      isHighContrast,
      accentId,
      toggleDarkMode,
      setAccentId,
      toggleHighContrast,
    ]
  );

  return (
    <ThemeContext.Provider value={value}>
      <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useAppTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useAppTheme must be used within AppThemeProvider");
  }
  return ctx;
}

export { md3Expressive };
